import './conversation.css';
import { state, settings } from '../shared/state';
import { $, t, showToast } from '../shared/index';
import { sessionStore } from '../session/session-store';
import { GenerationManager, registerConversationView } from '../generation-manager';
import { SessionManager, registerSessionConversationUi } from '../session/sessions';
import { showRegistrationPrompt } from '../shared/navigation';
import { uploadSessionAttachment, hasPendingAttachments, restoreSessionAttachments } from '../session/session-attachments';
import { filterSupportedDocumentFiles, parseDocumentFile } from '../kb/file-parse';
import { updateContextBadge, updateTokenInfo, updateWelcomeHints, autoResizeTextarea, renderMarkdown, renderDocChip, estimateTokens, getTotalDocTokens, setWelcomeVisible, showTypingIndicator } from '../shared/ui';
import { applyCategorySystemPrompt } from '../shared/system-prompt';
import type { AttachedDoc, MessageRole } from '../shared/types';

const isGuestMode = () => localStorage.getItem('avgexpert_guest_mode') === '1';


export async function newConversation(reloadList = true) {
  sessionStore.captureDraftFromUI();
  const active = sessionStore.getActiveSession();
  if (active) {
    await SessionManager.saveSession(active.id);
  }

  sessionStore.setActiveSession(null);
  const messagesEl = $('messages');
  const attachedDocsEl = $('attached-docs');
  if (messagesEl) messagesEl.textContent = '';
  if (attachedDocsEl) attachedDocsEl.textContent = '';
  setWelcomeVisible(true);
  updateWelcomeHints();

  const userInput = $<HTMLTextAreaElement>('user-input');
  if (userInput) userInput.value = '';

  updateContextBadge();
  updateTokenInfo();
  GenerationManager.onActiveSessionSwitched();

  if (reloadList && (state.authToken || isGuestMode())) {
    await SessionManager.loadList();
  }
}

function renderStreamingAssistantPlaceholder(session: NonNullable<ReturnType<typeof sessionStore.getActiveSession>>) {
  const partial = session.generation.partialAssistantText;
  const msgEl = addMessageToUI('assistant', partial, [], session.generation.categoryName, undefined, { autoScroll: false });
  const contentEl = msgEl.querySelector('.msg-content') as HTMLElement | null;
  if (!contentEl) return;
  if (partial) {
    renderMarkdown(contentEl, partial);
  } else {
    showTypingIndicator(contentEl);
  }
}

export function renderActiveConversationView() {
  const session = sessionStore.getActiveSession();
  const sessionId = session?.id;
  const messagesEl = $('messages');
  const attachedDocsEl = $('attached-docs');
  if (!messagesEl) return;

  messagesEl.textContent = '';
  if (!session || session.history.length === 0) {
    const streaming = session?.generation.status === 'streaming';
    setWelcomeVisible(!streaming);
    if (streaming && session) {
      renderStreamingAssistantPlaceholder(session);
    }
  } else {
    setWelcomeVisible(false);
    session.history.forEach((m) => {
      const displayText = m.role === 'user' && m.docs?.length
        ? extractUserDisplayText(m.content, m.docs)
        : m.content;
      addMessageToUI(m.role, displayText, [], m.categoryName || '', m.id, { autoScroll: false });
    });
    if (session.generation.status === 'streaming') {
      renderStreamingAssistantPlaceholder(session);
    }
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  if (attachedDocsEl) {
    attachedDocsEl.textContent = '';
    if (sessionId && sessionStore.activeSessionId === sessionId) {
      session.attachedDocs.forEach((d, i) => {
        renderDocChip(d, i, sessionId);
      });
    }
  }

  sessionStore.applyDraftToUI();
  autoResizeTextarea();
}

function extractUserDisplayText(content: string, docs: AttachedDoc[]) {
  if (!docs.length) return content;
  const sep = '\n\n---\n\n';
  const idx = content.lastIndexOf(sep);
  if (idx >= 0) return content.slice(idx + sep.length);
  return content;
}

export async function handleFiles(fileList: FileList | File[] | null) {
  if (!fileList) return;
  const session = sessionStore.getActiveSession();
  if (!session) {
    const id = crypto.randomUUID();
    sessionStore.setActiveSession(id);
  }
  const activeSession = sessionStore.getActiveSession()!;
  const sessionId = activeSession.id;

  const files = filterSupportedDocumentFiles(fileList);
  const useRag = isRagEffectiveForConversation();

  for (const file of files) {
    if (activeSession.attachedDocs.length >= state.maxDocsAllowed) {
      showToast(`Максимум ${state.maxDocsAllowed} документов для вашей категории`);
      break;
    }
    try {
      const text = await parseDocumentFile(file);
      if (!text?.trim()) {
        showToast(`Не удалось извлечь текст из «${file.name}»`, 'error');
        continue;
      }
      if (useRag) {
        const uploaded = await uploadSessionAttachment(sessionId, file, text);
        if (!uploaded) continue;
      } else {
        const tokens = estimateTokens(text);
        activeSession.attachedDocs.push({ name: file.name, text, tokens });
        const idx = activeSession.attachedDocs.length - 1;
        renderDocChip({ name: file.name, tokens }, idx, sessionId);
      }
      activeSession.dirty = true;
    } catch (e) {
      console.error('File attach error:', e);
      showToast('Не удалось обработать файл', 'error');
    }
  }
  const fileInput = $<HTMLInputElement>('file-input');
  if (fileInput) fileInput.value = '';
  updateTokenInfo();
  checkDocsFitContext();
  updateContextBadge();
  SessionManager.refreshSessionIndicators();
}

export async function parseFile(file: File) {
  return parseDocumentFile(file);
}

export function checkDocsFitContext() {
  const docTokens = getTotalDocTokens();
  const sysTokens = estimateTokens(settings.system_prompt);
  const total = docTokens + sysTokens + 200;
  if (total > state.contextSize) {
    showToast(t('doc_too_large', { tokens: total, ctx: state.contextSize }), { variant: 'info', duration: 20000 });
  }
}

function getActiveCategoryData() {
  const categoryName = $<HTMLSelectElement>('conversation-session-category')?.value || state.currentUser?.category || '';
  return state.categories?.[categoryName] || {};
}

function isRagEffectiveForConversation() {
  const cat = getActiveCategoryData();
  return cat.rag_allowed === true || cat.rag_allowed === 1;
}

export async function handleSend() {
  const userInput = $<HTMLTextAreaElement>('user-input');
  if (!userInput) return;
  const text = userInput.value.trim();
  if (!text) return;

  let session = sessionStore.getActiveSession();
  if (!session) {
    const id = crypto.randomUUID();
    session = sessionStore.setActiveSession(id)!;
    session.category = $<HTMLSelectElement>('conversation-session-category')?.value || state.currentUser?.category || '';
  }

  if (session.generation.status === 'streaming') return;
  if (hasPendingAttachments(session.id)) {
    showToast('Дождитесь завершения индексации вложений', 'error');
    return;
  }
  if (!state.authToken && !isGuestMode()) {
    showRegistrationPrompt();
    return;
  }

  setWelcomeVisible(false);
  await GenerationManager.startGeneration(session.id, text);
}

export function stopGeneration() {
  GenerationManager.stopGeneration();
}

export function buildExtraParams(): Record<string, unknown> | null {
  const params: Record<string, unknown> = {};
  if ($<HTMLInputElement>('feat-reasoning')?.checked) params.reasoning = {};
  if ($<HTMLInputElement>('feat-websearch')?.checked) {
    params.tools = [{ type: 'web_search' }];
    params.tool_choice = 'auto';
    params.enable_search = true;
  }
  return Object.keys(params).length > 0 ? params : null;
}

export function initMessagesDelegation() {
  const messagesEl = $('messages');
  if (!messagesEl || messagesEl.dataset.delegated) return;
  messagesEl.dataset.delegated = '1';

  messagesEl.addEventListener('click', (e: MouseEvent) => {
    const target = e.target as Element;

    // Copy action
    const copyBtn = target.closest('.copy-btn');
    if (copyBtn) {
      const body = copyBtn.closest('.msg-body') as HTMLElement | null;
      const contentEl = body?.querySelector('.msg-content') as HTMLElement | null;
      if (!contentEl || !body) return;
      let raw = contentEl.dataset.raw || contentEl.textContent || '';
      const catName = body.dataset.categoryName;
      if (catName) raw = catName + ':\n' + raw;
      navigator.clipboard.writeText(raw).then(() => {
        copyBtn.classList.add('copied');
        const span = copyBtn.querySelector('span');
        if (span) span.textContent = t('copied');
        setTimeout(() => {
          copyBtn.classList.remove('copied');
          if (span) span.textContent = t('copy');
        }, 2000);
      });
      return;
    }

    // Delete action
    const deleteBtn = target.closest('.delete-btn');
    if (deleteBtn) {
      if (isGuestMode()) return;
      const msgEl = deleteBtn.closest('.msg') as HTMLElement | null;
      const messageId = msgEl?.dataset.messageId;
      if (msgEl) {
        const session = sessionStore.getActiveSession();
        if (session) {
          if (messageId) {
            session.history = session.history.filter((m) => m.id !== messageId);
          } else {
            const allMsgs = Array.from(messagesEl.querySelectorAll('.msg'));
            const idx = allMsgs.indexOf(msgEl);
            if (idx >= 0) {
              session.history.splice(idx, 1);
            }
          }
          session.dirty = true;
          msgEl.remove();

          // Save updated session history
          SessionManager.saveSession(session.id);

          if (session.history.length === 0) {
            setWelcomeVisible(true);
          }
          updateContextBadge();
        }
      }
      return;
    }

    // Edit action
    const editBtn = target.closest('.edit-btn');
    if (editBtn) {
      const msgEl = editBtn.closest('.msg') as HTMLElement | null;
      const messageId = msgEl?.dataset.messageId;
      const body = editBtn.closest('.msg-body') as HTMLElement | null;
      const contentEl = body?.querySelector('.msg-content') as HTMLElement | null;
      if (!msgEl || !contentEl || !body || !messageId) return;

      const session = sessionStore.getActiveSession();
      if (!session) return;

      const messageObj = session.history.find((m) => m.id === messageId);
      if (!messageObj) return;

      const originalText = messageObj.content;

      // Create edit container and controls
      const editContainer = document.createElement('div');
      editContainer.className = 'msg-edit-container';

      const textarea = document.createElement('textarea');
      textarea.className = 'msg-edit-textarea';
      textarea.value = originalText;

      const actionsDiv = document.createElement('div');
      actionsDiv.className = 'msg-edit-actions';

      const saveBtn = document.createElement('button');
      saveBtn.className = 'msg-edit-btn save';
      saveBtn.textContent = t('save_msg');

      const cancelBtn = document.createElement('button');
      cancelBtn.className = 'msg-edit-btn cancel';
      cancelBtn.textContent = t('cancel');

      actionsDiv.appendChild(saveBtn);
      actionsDiv.appendChild(cancelBtn);
      editContainer.appendChild(textarea);
      editContainer.appendChild(actionsDiv);

      const originalContent = contentEl.innerHTML;
      const actionsEl = body.querySelector('.msg-actions') as HTMLElement | null;
      if (actionsEl) actionsEl.style.display = 'none';

      msgEl.classList.add('editing');
      contentEl.textContent = '';
      contentEl.appendChild(editContainer);

      textarea.focus();
      textarea.style.height = 'auto';
      textarea.style.height = (textarea.scrollHeight) + 'px';
      textarea.addEventListener('input', () => {
        textarea.style.height = 'auto';
        textarea.style.height = (textarea.scrollHeight) + 'px';
      });

      const finishEdit = (save: boolean) => {
        msgEl.classList.remove('editing');
        if (actionsEl) actionsEl.style.display = '';
        if (save) {
          const newText = textarea.value.trim();
          if (newText && newText !== originalText) {
            messageObj.content = newText;
            session.dirty = true;
            renderMarkdown(contentEl, newText);
            SessionManager.saveSession(session.id);
            updateContextBadge();
          } else {
            contentEl.innerHTML = originalContent;
          }
        } else {
          contentEl.innerHTML = originalContent;
        }
      };

      saveBtn.addEventListener('click', () => finishEdit(true));
      cancelBtn.addEventListener('click', () => finishEdit(false));

      textarea.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter' && (ev.ctrlKey || ev.metaKey)) {
          ev.preventDefault();
          finishEdit(true);
        }
        if (ev.key === 'Escape') {
          ev.preventDefault();
          finishEdit(false);
        }
      });

      return;
    }
  });
}

export function addMessageToUI(
  role: MessageRole,
  text: string,
  _docs: AttachedDoc[] = [],
  categoryName = '',
  messageId?: string,
  options: { autoScroll?: boolean } = {},
) {
  const msg = document.createElement('div');
  msg.className = 'msg ' + role;
  if (messageId) msg.dataset.messageId = messageId;
  const avatarText = role === 'user' ? '👤' : '✦';
  const avatarTitle = (role === 'assistant' && categoryName) ? ` title="${DOMPurify.sanitize(categoryName)}"` : '';

  const editBtnHtml = `
        <button class="msg-action-btn edit-btn" title="${t('edit')}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          <span>${t('edit')}</span>
        </button>`;

  const deleteBtnHtml = isGuestMode() ? '' : `
        <button class="msg-action-btn delete-btn" title="${t('delete')}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
          <span>${t('delete')}</span>
        </button>`;

  msg.innerHTML = `<div class="msg-avatar"${avatarTitle}>${avatarText}</div>
    <div class="msg-body"${categoryName ? ` data-category-name="${DOMPurify.sanitize(categoryName)}"` : ''}>
      <div class="msg-content"></div>
      <div class="msg-actions">
        <button class="msg-action-btn copy-btn" title="${t('copy')}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          <span>${t('copy')}</span>
        </button>
        ${editBtnHtml}
        ${deleteBtnHtml}
      </div>
    </div>`;

  const contentEl = msg.querySelector('.msg-content') as HTMLElement;
  if (text) renderMarkdown(contentEl, text);

  const messagesEl = $('messages');
  if (messagesEl) {
    messagesEl.appendChild(msg);
    if (options.autoScroll !== false) {
      messagesEl.scrollTop = messagesEl.scrollHeight;
    }
  }
  return msg;
}

export function scrollToMessage(messageId: string, query?: string) {
  const el = document.querySelector(`.msg[data-message-id="${messageId}"]`);
  if (!el) return false;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.add('msg-highlight');
  
  const contentEl = el.querySelector('.msg-content') as HTMLElement | null;
  if (contentEl && query && query.trim()) {
    // Clear any existing timeouts for this element to avoid conflicts
    if ((contentEl as any)._fadeTimeout) clearTimeout((contentEl as any)._fadeTimeout);
    if ((contentEl as any)._restoreTimeout) clearTimeout((contentEl as any)._restoreTimeout);
    
    // If it was already highlighted, restore it first before applying new highlight
    if (contentEl.querySelector('.text-match-highlight') && contentEl.dataset.raw) {
      renderMarkdown(contentEl, contentEl.dataset.raw);
    }
    
    highlightTextInNode(contentEl, query);
    
    (contentEl as any)._fadeTimeout = setTimeout(() => {
      contentEl.querySelectorAll('.text-match-highlight').forEach((mark) => {
        mark.classList.add('fade-out');
      });
    }, 4500);
    
    (contentEl as any)._restoreTimeout = setTimeout(() => {
      if (contentEl.dataset.raw) {
        renderMarkdown(contentEl, contentEl.dataset.raw);
      }
      delete (contentEl as any)._fadeTimeout;
      delete (contentEl as any)._restoreTimeout;
    }, 5000);
  }

  setTimeout(() => el.classList.remove('msg-highlight'), 2500);
  return true;
}

function highlightTextInNode(node: Node, query: string) {
  if (!query || !query.trim()) return;
  const escapedQuery = escapeRegExp(query.trim());
  const regex = new RegExp(`(${escapedQuery})`, 'gi');

  const walk = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
  const nodesToReplace: Text[] = [];
  let textNode: Text | null;

  while ((textNode = walk.nextNode() as Text | null)) {
    const parent = textNode.parentNode;
    if (parent && (parent.nodeName === 'MARK' || parent.nodeName === 'SCRIPT' || parent.nodeName === 'STYLE')) {
      continue;
    }
    if (textNode.textContent && regex.test(textNode.textContent)) {
      nodesToReplace.push(textNode);
    }
  }

  nodesToReplace.forEach((oldNode) => {
    const text = oldNode.textContent || '';
    const parent = oldNode.parentNode;
    if (!parent) return;

    const fragment = document.createDocumentFragment();
    const parts = text.split(regex);

    parts.forEach((part) => {
      if (regex.test(part)) {
        const mark = document.createElement('mark');
        mark.className = 'text-match-highlight';
        mark.textContent = part;
        fragment.appendChild(mark);
      } else if (part) {
        fragment.appendChild(document.createTextNode(part));
      }
    });

    parent.replaceChild(fragment, oldNode);
  });
}

function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// --- ADR-002 phase C: the generation engine and the session capability call
// back into conversation through these registrations instead of importing conversation/*.
registerConversationView({
  addMessageToUI,
  renderActiveConversationView,
  buildExtraParams,
});
registerSessionConversationUi({
  renderActiveConversationView,
  startNewConversation: (reloadList?: boolean) => newConversation(reloadList),
});
